import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, HeartHandshake, Home as HomeIcon, MessageCircle,
  Smartphone, Monitor, Wifi, Camera, Printer, KeyRound, GraduationCap, ArrowRight,
  Star, CheckCircle2,
} from "lucide-react";
import { SERVICES, TESTIMONIALS } from "../data";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CTABand } from "../components/CTABand";
import { HomeHero } from "../components/home/HomeHero";

const ICONS = { Smartphone, Monitor, Wifi, Camera, Printer, KeyRound, GraduationCap };

const VALUES = [
  { icon: HeartHandshake, title: "Patient & Kind", text: "We never rush and never judge. Ask anything, as many times as you like." },
  { icon: MessageCircle, title: "Plain English", text: "No confusing jargon — just clear, simple explanations you'll actually remember." },
  { icon: HomeIcon, title: "We Come to You", text: "In‑home visits across Columbus, plus friendly phone and video help." },
  { icon: ShieldCheck, title: "Honest & Upfront", text: "Clear pricing before we start. No surprises, ever." },
];

const CHAPTERS = [
  { n: "01", t: "We listen, first", d: "Tell us what's giving you trouble in your own words. We never make you feel silly for asking." },
  { n: "02", t: "We explain plainly", d: "No jargon, no talking down. Just clear steps in language that actually makes sense." },
  { n: "03", t: "We move at your pace", d: "As slow or as thorough as you need. We'll happily go over anything a second time." },
  { n: "04", t: "We stay in your corner", d: "Questions after we leave? Just call. We're always glad to help you again." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <HomeHero />

      {/* Editorial marquee */}
      <Marquee
        items={[
          "Patient", "Plain English", "In‑Home Visits", "Judgement‑Free",
          "Local to Columbus", "Friendly Faces",
        ]}
      />

      {/* Values */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="h-full rounded-2xl border border-border bg-card p-8 hover:border-accent/40 hover:shadow-xl"
              >
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent/10 text-accent mb-5">
                  <v.icon className="h-7 w-7" />
                </span>
                <h3 className="font-heading font-bold text-xl text-foreground mb-2">{v.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{v.text}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="bg-secondary">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <Reveal>
            <div className="max-w-2xl">
              <p className="text-accent font-bold uppercase tracking-wide mb-3">What we help with</p>
              <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug">
                Everyday tech, handled with care
              </h2>
              <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
                From a brand‑new phone to a whole‑home Wi‑Fi setup, we take care of
                the technical parts and show you the rest.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    data-testid={`home-service-${s.slug}`}
                    className="h-full rounded-2xl bg-card border border-border p-8 hover:shadow-xl"
                  >
                    <span className="grid place-items-center h-14 w-14 rounded-2xl bg-primary text-white mb-5">
                      <Icon className="h-7 w-7" />
                    </span>
                    <h3 className="font-heading font-bold text-xl text-foreground mb-3">{s.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{s.short}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-12">
            <Link
              to="/services"
              data-testid="home-view-all-services"
              className="inline-flex items-center gap-2 text-accent font-bold text-lg hover:gap-3 transition-all"
            >
              See all services <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Manifesto chapters */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-32 grid lg:grid-cols-[0.8fr,1.2fr] gap-14">
        <div>
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <p className="text-accent font-bold uppercase tracking-wide mb-3">How we help</p>
              <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                A gentler way to get tech help
              </h2>
              <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-md">
                Four simple promises that guide every visit, call, and lesson.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="space-y-4">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.06}>
              <div className="group grid grid-cols-[auto,1fr] gap-6 sm:gap-10 items-start rounded-2xl border border-border bg-card p-8 lg:p-10 hover:border-accent/40 transition-colors">
                <span className="font-heading font-extrabold text-4xl sm:text-5xl text-accent/25 group-hover:text-accent transition-colors leading-none">
                  {c.n}
                </span>
                <div className="pt-1">
                  <h3 className="font-heading font-bold text-2xl text-foreground mb-2">{c.t}</h3>
                  <p className="text-muted-foreground text-lg leading-relaxed">{c.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Senior focus */}
      <section className="bg-primary overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] shadow-2xl h-[380px] lg:h-[460px]">
              <img
                src="https://images.unsplash.com/photo-1587556930799-8dca6fad6d41?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Friendly helper showing a phone to a senior"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-accent font-bold uppercase tracking-wide mb-3">Especially for seniors</p>
            <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug">
              Respectful help, at your pace
            </h2>
            <p className="mt-5 text-slate-300 text-lg leading-relaxed">
              We specialize in helping seniors feel confident with technology. We
              take our time, write down simple steps, and are always happy to
              explain things more than once.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "No rushing and no judgement — ever",
                "Big, clear notes you can keep",
                "Call us anytime with follow‑up questions",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-200 text-lg">
                  <CheckCircle2 className="h-6 w-6 text-accent shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
            <Link
              to="/senior-tech-help"
              data-testid="home-senior-link"
              className="mt-9 inline-flex items-center justify-center h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors"
            >
              Learn about Senior Tech Help
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Testimonials preview */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <Reveal>
          <div className="max-w-2xl">
            <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug">
              Loved by our neighbors
            </h2>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
              Real words from real people we've helped.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.slice(0, 3).map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-border bg-card p-8 flex flex-col">
                <div className="flex text-accent mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-current" />
                  ))}
                </div>
                <p className="text-slate-300 text-lg leading-relaxed flex-1">“{t.quote}”</p>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="font-bold text-foreground">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
